import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createUserWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, query, where, collection, getDocs } from 'firebase/firestore';
import { auth, db, googleProvider } from '../lib/firebase';
import { UserPlus, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const InvitationRegister = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [invitation, setInvitation] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validatingInvitation, setValidatingInvitation] = useState(true);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    // Get invitation token from URL
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('invitation');
    
    if (token) {
      validateInvitation(token);
    } else {
      setError('Token de invitación no encontrado');
      setValidatingInvitation(false);
    }
  }, [location]);

  const validateInvitation = async (token) => {
    try {
      const q = query(collection(db, 'invitations'), where('token', '==', token));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setError('Invitación no encontrada');
        setValidatingInvitation(false);
        return;
      }

      const invitationDoc = querySnapshot.docs[0];
      const invitationData = invitationDoc.data();
      
      // Check if invitation is still valid
      if (invitationData.status !== 'pending') {
        setError('Esta invitación ya ha sido utilizada');
        setValidatingInvitation(false);
        return;
      }

      // Check if invitation has expired
      const now = new Date();
      const expiresAt = invitationData.expiresAt.toDate();
      if (now > expiresAt) {
        setError('Esta invitación ha expirado');
        setValidatingInvitation(false);
        return;
      }

      // Set invitation data and prefill email
      setInvitation({ id: invitationDoc.id, ...invitationData });
      setFormData(prev => ({ ...prev, email: invitationData.email }));
      setValidatingInvitation(false);
    } catch (error) {
      console.error('Error validating invitation:', error);
      setError('Error al validar la invitación');
      setValidatingInvitation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!invitation) {
      setError('No hay invitación válida');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );

      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: formData.email,
        displayName: formData.displayName,
        role: invitation.role,
        createdAt: new Date(),
        status: 'active',
        invitedBy: invitation.createdBy || null
      });

      // Mark invitation as used
      await updateDoc(doc(db, 'invitations', invitation.id), {
        status: 'used',
        usedAt: new Date(),
        usedBy: userCredential.user.uid
      });

      setSuccess('Cuenta creada exitosamente. Será redirigido automáticamente...');
      
      // Redirect after successful registration
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Registration error:', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Este email ya está en uso');
          break;
        case 'auth/invalid-email':
          setError('Email inválido');
          break;
        case 'auth/weak-password':
          setError('La contraseña es muy débil');
          break;
        default:
          setError('Error al crear la cuenta');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    if (!invitation) {
      setError('No hay invitación válida');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Sign in with Google
      const userCredential = await signInWithPopup(auth, googleProvider);
      
      // Check if the Google account email matches the invitation email
      if (userCredential.user.email !== invitation.email) {
        // Sign out the user since the email doesn't match
        await auth.signOut();
        setError('El email de la cuenta de Google no coincide con el email de la invitación');
        setLoading(false);
        return;
      }

      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
        role: invitation.role,
        createdAt: new Date(),
        status: 'active',
        invitedBy: invitation.createdBy || null
      });

      // Mark invitation as used
      await updateDoc(doc(db, 'invitations', invitation.id), {
        status: 'used',
        usedAt: new Date(),
        usedBy: userCredential.user.uid
      });

      setSuccess('Cuenta creada exitosamente con Google. Será redirigido automáticamente...');
      
      // Redirect after successful registration
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Google registration error:', error);
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          setError('La ventana de Google se cerró. Intente nuevamente.');
          break;
        case 'auth/popup-blocked':
          setError('El popup fue bloqueado. Permita popups para este sitio.');
          break;
        case 'auth/cancelled-popup-request':
          setError('Solicitud cancelada. Intente nuevamente.');
          break;
        case 'auth/account-exists-with-different-credential':
          setError('Ya existe una cuenta con este email usando un método diferente.');
          break;
        default:
          setError('Error al crear la cuenta con Google');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8">
          <div className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ya tienes una cuenta</h2>
            <p className="text-gray-600 mb-6">Ya estás autenticado en el sistema</p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Ir al Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (validatingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Validando invitación...</h2>
            <p className="text-gray-600">Por favor espere mientras validamos su invitación</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8">
          <div className="text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invitación Inválida</h2>
            <p className="text-red-600 mb-6">{error}</p>
            <p className="text-gray-600 text-sm">
              Contacta con el administrador del sistema para obtener una nueva invitación.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-600">
            <UserPlus className="h-6 w-6 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Crear Cuenta
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Has sido invitado a STB Workshop como{' '}
            <span className="font-medium text-blue-600">
              {invitation?.role === 'admin' ? 'Administrador' : 
               invitation?.role === 'shopkeeper' ? 'Shopkeeper' : 'Solo Lectura'}
            </span>
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
              {success}
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleGoogleRegister}
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? 'Registrando...' : 'Registrarse con Google'}
            </button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">o</span>
              </div>
            </div>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                Nombre Completo
              </label>
              <input
                id="displayName"
                name="displayName"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Su nombre completo"
                value={formData.displayName}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                disabled
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-50"
                value={formData.email}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Contraseña
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Su contraseña"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirmar Contraseña
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirme su contraseña"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
{loading ? 'Creando cuenta...' : 'Crear Cuenta con Email'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvitationRegister;